import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import InvitationDetailsCard from './components/InvitationDetailsCard';
import AccountSetupForm from './components/AccountSetupForm';

const InvitationAcceptance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams?.get('token');

  const [invitation, setInvitation] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | valid | invalid | expired | accepted | declined
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      setErrorMessage('No invitation token provided. Please use the link from your invitation email.');
      return;
    }
    loadInvitation(token);
  }, [token]);

  const loadInvitation = async (invToken) => {
    try {
      const { data, error } = await supabase?.from('invitations')?.select('*, invited_by_profile:user_profiles!invitations_invited_by_fkey(full_name)')?.eq('invitation_token', invToken)?.single();

      if (error || !data) {
        // Try without join in case FK name differs
        const { data: fallback, error: fallbackErr } = await supabase?.from('invitations')?.select('*')?.eq('invitation_token', invToken)?.single();

        if (fallbackErr || !fallback) {
          setStatus('invalid');
          setErrorMessage('Invitation not found. It may have already been used or the link is incorrect.');
          return;
        }

        processInvitation(fallback, null);
        return;
      }

      processInvitation(data, data?.invited_by_profile);
    } catch (err) {
      setStatus('invalid');
      setErrorMessage('Unable to load invitation. Please try again.');
    }
  };

  const processInvitation = (data, inviterProfile) => {
    if (data?.status === 'accepted') {
      setStatus('accepted');
      setSuccessMessage('This invitation has already been accepted.');
      return;
    }
    if (data?.status === 'declined') {
      setStatus('declined');
      setErrorMessage('This invitation has already been declined.');
      return;
    }
    if (new Date(data.expires_at) < new Date()) {
      setStatus('expired');
      setErrorMessage('This invitation has expired. Please ask your administrator to send a new invitation.');
      return;
    }

    setInvitation({
      ...data,
      invited_by_name: inviterProfile?.full_name || 'A team administrator',
      company_name: 'ConstructionPro',
    });
    setStatus('valid');
  };

  const handleAccept = async ({ fullName, password }) => {
    setSubmitting(true);
    try {
      // Check if the user is already authenticated (Supabase invite link flow)
      // When Supabase's inviteUserByEmail is used, clicking the email link
      // authenticates the user automatically before redirecting here.
      const { data: sessionData } = await supabase?.auth?.getSession();
      const existingUser = sessionData?.session?.user;

      let userId = null;

      if (existingUser) {
        // User is already authenticated via Supabase invite — just set their password
        const { error: updateError } = await supabase?.auth?.updateUser({
          password,
          data: { full_name: fullName },
        });

        if (updateError) {
          throw new Error(updateError?.message || 'Failed to set password. Please try again.');
        }
        userId = existingUser?.id;
      } else {
        // No existing session — sign up the user normally (manual token flow)
        const { data: signUpData, error: signUpError } = await supabase?.auth?.signUp({
          email: invitation?.email,
          password,
          options: {
            data: { full_name: fullName },
          },
        });

        if (signUpError) {
          // If user already exists, try signing in and updating
          if (signUpError?.message?.includes('already registered') || signUpError?.message?.includes('already been registered')) {
            const { error: signInError } = await supabase?.auth?.signInWithPassword({
              email: invitation?.email,
              password,
            });
            if (signInError) {
              throw new Error('An account with this email already exists. Please sign in with your existing password.');
            }
          } else {
            throw signUpError;
          }
        }
        userId = signUpData?.user?.id;
      }

      // Update invitation status
      await supabase?.from('invitations')?.update({ status: 'accepted' })?.eq('invitation_token', token);

      // Update user profile if we have a userId
      if (userId) {
        await supabase?.from('user_profiles')?.upsert({
            id: userId,
            full_name: fullName,
            email: invitation?.email,
            role: invitation?.role,
          }, { onConflict: 'id' });
      }

      setStatus('success');
      setSuccessMessage(`Welcome to ConstructionPro, ${fullName}! Your account has been created successfully.`);

      // Redirect to profile setup after short delay
      setTimeout(() => {
        navigate('/profile-setup', { replace: true, state: { fromInvitation: true, name: fullName } });
      }, 2500);
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async (reason) => {
    setSubmitting(true);
    try {
      await supabase?.from('invitations')?.update({ status: 'declined' })?.eq('invitation_token', token);

      setStatus('declined');
      setSuccessMessage('You have declined the invitation.');
    } catch (err) {
      setErrorMessage('Failed to decline invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Logo / Brand */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl mb-3 shadow-lg">
            <Icon name="HardHat" size={28} color="#FFFFFF" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">ConstructionPro</h1>
          <p className="text-sm text-gray-500 mt-1">Team Invitation</p>
        </div>

        {/* Loading */}
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center">
            <div className="flex items-center justify-center mb-4">
              <Icon name="Loader2" size={32} className="text-amber-500 animate-spin" />
            </div>
            <p className="text-gray-600 font-medium">Loading your invitation...</p>
          </div>
        )}

        {/* Invalid / Expired / Declined */}
        {(status === 'invalid' || status === 'expired' || status === 'declined') && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <Icon
                name={status === 'expired' ? 'Clock' : status === 'declined' ? 'XCircle' : 'AlertTriangle'}
                size={28}
                className="text-red-500"
              />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {status === 'expired' ? 'Invitation Expired' : status === 'declined' ? 'Invitation Declined' : 'Invalid Invitation'}
            </h2>
            <p className="text-gray-600 text-sm mb-6">{errorMessage}</p>
            <button
              onClick={() => navigate('/auth/signin')}
              className="text-amber-600 hover:text-amber-700 text-sm font-medium underline"
            >
              Go to Sign In
            </button>
          </div>
        )}

        {/* Already Accepted */}
        {status === 'accepted' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={28} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Already Accepted</h2>
            <p className="text-gray-600 text-sm mb-6">{successMessage}</p>
            <button
              onClick={() => navigate('/auth/signin')}
              className="text-amber-600 hover:text-amber-700 text-sm font-medium underline"
            >
              Sign In to your account
            </button>
          </div>
        )}

        {/* Success */}
        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="PartyPopper" size={28} className="text-green-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Welcome Aboard! 🎉</h2>
            <p className="text-gray-600 text-sm mb-4">{successMessage}</p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Icon name="Loader2" size={14} className="animate-spin" />
              Redirecting to dashboard...
            </div>
          </div>
        )}

        {/* Valid — Show invitation + form */}
        {status === 'valid' && invitation && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Page Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
              <h2 className="text-xl font-bold text-gray-900">You've Been Invited!</h2>
              <p className="text-sm text-gray-500 mt-1">
                Review the details below and complete your account setup to join the team.
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Invitation Details */}
              <InvitationDetailsCard invitation={invitation} />

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs text-gray-400 font-medium uppercase tracking-wider">Account Setup</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Error message */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="AlertCircle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              )}

              {/* Account Setup Form */}
              <AccountSetupForm
                onAccept={handleAccept}
                onDecline={handleDecline}
                loading={submitting}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/auth/signin')}
            className="text-amber-600 hover:text-amber-700 font-medium"
          >
            Sign in here
          </button>
        </p>
      </div>
    </div>
  );
};

export default InvitationAcceptance;
